import React from 'react';

import { Sun, Moon } from 'lucide-react';

import { useDarkMode } from '../../contexts/DarkModeContext';

interface ThemeToggleProps {
  className?: string;
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({
  className = '',
  showLabel = false,
  size = 'md',
}) => {
  const { theme, toggleTheme } = useDarkMode();
  const isDark = theme === 'dark';

  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-10 w-10',
    lg: 'h-12 w-12',
  };

  const iconSizes = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
  };

  return (
    <button
      onClick={toggleTheme}
      className={`
        ${sizeClasses[size]}
        relative
        rounded-xl
        border
        border-neutral-200
        dark:border-neutral-700
        bg-white
        dark:bg-neutral-800
        text-neutral-700
        dark:text-neutral-300
        hover:bg-neutral-50
        dark:hover:bg-neutral-700
        transition-all
        duration-300
        ease-in-out
        flex
        items-center
        justify-center
        focus:outline-none
        focus:ring-2
        focus:ring-amber-500
        focus:ring-offset-2
        dark:focus:ring-offset-neutral-900
        ${className}
      `}
      aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
      title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
    >
      <div className="relative">
        <Sun
          className={`
          ${iconSizes[size]}
          absolute
          left-1/2
          top-1/2
          -translate-x-1/2
          -translate-y-1/2
          transition-all
          duration-300
          ease-in-out
          ${isDark ? 'opacity-0 rotate-90 scale-0' : 'opacity-100 rotate-0 scale-100'}
        `}
        />
        <Moon
          className={`
          ${iconSizes[size]}
          absolute
          left-1/2
          top-1/2
          -translate-x-1/2
          -translate-y-1/2
          transition-all
          duration-300
          ease-in-out
          ${isDark ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-0'}
        `}
        />
      </div>
      {showLabel && <span className="ml-2 text-sm font-medium">{isDark ? 'Dark' : 'Light'}</span>}
    </button>
  );
};

export default ThemeToggle;
